import { useState, useRef } from "react";
import { Plus, Camera, User, Sliders, Key, LogOut, Bug, Check, X } from "lucide-react";
import { PageInfoButton } from "@/components/common/page-info-button";
import { useAuthStore } from "@/stores/auth-store";
import { useTheme } from "@/hooks/use-theme";
import { usePageTitle } from "@/hooks/use-page-title";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTokens, useRevokeToken } from "@/hooks/queries/use-tokens";
import { useUpdateUser } from "@/hooks/queries/use-users";
import {
  useMe,
  useStoreFogbugzKey,
  useClearFogbugzKey,
  useUpdatePreferredHost,
} from "@/hooks/queries/use-me";
import {
  PREFERRED_HOSTS,
  PREFERRED_HOST_LABELS,
  resolvePreferredHost,
  type PreferredHost,
} from "@spectree/shared";
import { useToast } from "@/hooks/useToast";
import { getInitials } from "@/lib/get-initials";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiKeyList } from "@/components/settings/api-key-list";
import { CreateApiKeyDialog } from "@/components/settings/create-api-key-dialog";
import { AdoPatSection } from "@/components/settings/ado-pat-section";
import { allTimeZones } from "@/lib/time-zones";

/** Common IANA timezone identifiers. Uses the runtime's full list when available. */
const TIMEZONE_OPTIONS: string[] = allTimeZones();

const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function SettingsPage() {
  usePageTitle("Settings");
  const isMobile = useIsMobile();
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useTheme();
  const [name, setName] = useState(user?.name ?? "");
  const [timeZone, setTimeZone] = useState(user?.timeZone ?? browserTimeZone);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ?? "");
  const [isCreateKeyDialogOpen, setIsCreateKeyDialogOpen] = useState(false);
  const [fogbugzKey, setFogbugzKey] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: tokens, isLoading: isLoadingTokens } = useTokens();
  const { data: meData, isLoading: isLoadingMe } = useMe();
  const revokeToken = useRevokeToken();
  const updateUser = useUpdateUser();
  const storeFogbugzKey = useStoreFogbugzKey();
  const clearFogbugzKey = useClearFogbugzKey();
  const updatePreferredHost = useUpdatePreferredHost();
  const { toast } = useToast();

  const hasFogbugzKey = meData?.hasFogbugzKey ?? false;
  const preferredHost = resolvePreferredHost(meData?.preferredHost);

  const handlePreferredHostChange = (value: string) => {
    updatePreferredHost.mutate(
      { host: value as PreferredHost },
      {
        onSuccess: () => {
          toast(`CLI host set to ${PREFERRED_HOST_LABELS[value as PreferredHost]}`);
        },
      }
    );
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      alert("Image must be under 512 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setAvatarUrl(result);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = () => {
    if (!user) return;
    updateUser.mutate(
      { id: user.id, data: { name, timeZone, ...(avatarUrl ? { avatarUrl } : {}) } },
      {
        onSuccess: (res) => {
          // Merge with existing user to preserve fields not returned by PUT /users/:id
          // (e.g. role, teamId, isGlobalAdmin from membership join in /auth/me)
          const current = useAuthStore.getState().user;
          useAuthStore.setState({
            user: {
              ...(current ?? {}),
              ...res.data,
              // Preserve auth-critical fields from the existing store when available
              ...(current
                ? {
                    isGlobalAdmin: current.isGlobalAdmin,
                    role: current.role,
                    teamId: current.teamId,
                  }
                : {}),
            },
          });
        },
      }
    );
  };

  const handleStoreFogbugzKey = () => {
    if (!fogbugzKey.trim()) return;
    storeFogbugzKey.mutate(
      { apiKey: fogbugzKey },
      {
        onSuccess: () => {
          setFogbugzKey("");
          toast("FogBugz API key saved");
        },
        onError: (err) => {
          toast(
            err instanceof Error
              ? `Failed to save FogBugz key: ${err.message}`
              : "Failed to save FogBugz key"
          );
        },
      }
    );
  };

  const handleClearFogbugzKey = () => {
    clearFogbugzKey.mutate(undefined, {
      onSuccess: () => {
        toast("FogBugz API key cleared");
      },
      onError: (err) => {
        toast(
          err instanceof Error
            ? `Failed to clear FogBugz key: ${err.message}`
            : "Failed to clear FogBugz key"
        );
      },
    });
  };

  return (
    <div className="max-w-5xl mx-auto pt-4 px-4 md:px-0">
      {/* Header */}
      <div className={isMobile ? "mb-6 space-y-3" : "flex items-center justify-between mb-8"}>
        <div className="flex items-center gap-1">
          <h1 className="text-xl font-semibold">Settings</h1>
          <PageInfoButton title="Settings" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground truncate">{user?.email}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              logout();
            }}
            className="text-muted-foreground hover:text-foreground gap-1.5"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </Button>
        </div>
      </div>

      {/* Bento grid — single column on mobile */}
      <div className={isMobile ? "space-y-4" : "grid grid-cols-2 gap-4"}>
        {/* Account — spans left column */}
        <div
          className={`glass-card rounded-2xl border border-white/[0.08] p-5 md:p-6 ${isMobile ? "" : "row-span-2"}`}
        >
          <div className="flex items-center gap-2 mb-5">
            <User className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-medium">Account</h2>
          </div>

          <div className="flex flex-col items-center mb-6">
            <div className="relative group">
              <Avatar className="h-24 w-24">
                {avatarUrl && <AvatarImage src={avatarUrl} alt={name} />}
                <AvatarFallback className="text-2xl bg-primary/15 text-primary font-medium">
                  {getInitials(name || user?.name || "?")}
                </AvatarFallback>
              </Avatar>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <Camera className="h-5 w-5 text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Click to change · Max 512 KB</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs text-muted-foreground">
                Display Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                className="bg-white/[0.04] border-white/[0.08]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                value={user?.email ?? ""}
                disabled
                className="bg-white/[0.04] border-white/[0.08] opacity-60"
              />
            </div>
            <Button
              onClick={() => {
                handleSaveProfile();
              }}
              disabled={updateUser.isPending}
              size="sm"
              className="w-full"
            >
              {updateUser.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>

        {/* Preferences — top right */}
        <div className="glass-card rounded-2xl border border-white/[0.08] p-6">
          <div className="flex items-center gap-2 mb-5">
            <Sliders className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-medium">Preferences</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between opacity-50">
              <div>
                <div className="text-sm">Theme</div>
                <div className="text-xs text-muted-foreground">Appearance mode</div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    role="group"
                    aria-label="Theme selector — coming soon"
                    className="inline-block"
                  >
                    <Select value={theme} onValueChange={setTheme} disabled>
                      <SelectTrigger className="w-28 bg-white/[0.04] border-white/[0.08] cursor-not-allowed">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="light">Light</SelectItem>
                        <SelectItem value="dark">Dark</SelectItem>
                        <SelectItem value="system">System</SelectItem>
                      </SelectContent>
                    </Select>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Coming soon</TooltipContent>
              </Tooltip>
            </div>

            <div className="h-px bg-white/[0.06]" />

            <div className={isMobile ? "space-y-2" : "flex items-center justify-between"}>
              <div>
                <div className="text-sm">Timezone</div>
                <div className="text-xs text-muted-foreground">Dates &amp; activity</div>
              </div>
              <Select value={timeZone} onValueChange={setTimeZone}>
                <SelectTrigger
                  className={`${isMobile ? "w-full" : "w-52"} bg-white/[0.04] border-white/[0.08]`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz.replace(/_/g, " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="h-px bg-white/[0.06]" />

            <div className={isMobile ? "space-y-2" : "flex items-center justify-between"}>
              <div>
                <div className="text-sm">CLI host</div>
                <div className="text-xs text-muted-foreground">
                  Commands &amp; agent guidance
                </div>
              </div>
              <Select
                value={preferredHost}
                onValueChange={handlePreferredHostChange}
                disabled={isLoadingMe || updatePreferredHost.isPending}
              >
                <SelectTrigger
                  className={`${isMobile ? "w-full" : "w-52"} bg-white/[0.04] border-white/[0.08]`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PREFERRED_HOSTS.map((host) => (
                    <SelectItem key={host} value={host}>
                      {PREFERRED_HOST_LABELS[host]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* API Keys — bottom right */}
        <div className="glass-card rounded-2xl border border-white/[0.08] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-medium">API Keys</h2>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsCreateKeyDialogOpen(true);
              }}
              className="h-7 text-xs gap-1"
            >
              <Plus className="h-3 w-3" />
              New key
            </Button>
          </div>

          {isLoadingTokens ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
              Loading...
            </div>
          ) : (
            <>
              {tokens && tokens.length > 0 && (
                <div className="text-xs text-muted-foreground mb-3">
                  {tokens.length} {tokens.length === 1 ? "key" : "keys"}
                </div>
              )}
              <ApiKeyList
                tokens={tokens ?? []}
                onRevoke={(id) => {
                  revokeToken.mutate(id);
                }}
                isRevoking={revokeToken.isPending}
              />
            </>
          )}
        </div>

        {/* FogBugz Integration — below API Keys */}
        <div className="glass-card rounded-2xl border border-white/[0.08] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Bug className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-medium">FogBugz Integration</h2>
          </div>

          {isLoadingMe ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
              Loading...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {hasFogbugzKey ? (
                  <>
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-muted-foreground">API key configured</span>
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">No API key configured</span>
                  </>
                )}
              </div>

              {hasFogbugzKey ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Your FogBugz API key is securely stored and encrypted.
                  </p>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleClearFogbugzKey}
                    disabled={clearFogbugzKey.isPending}
                    className="w-full"
                  >
                    {clearFogbugzKey.isPending ? "Clearing..." : "Clear API Key"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="fogbugz-key" className="text-xs text-muted-foreground">
                    FogBugz API Key
                  </Label>
                  <Input
                    id="fogbugz-key"
                    type="password"
                    value={fogbugzKey}
                    onChange={(e) => {
                      setFogbugzKey(e.target.value);
                    }}
                    placeholder="Enter your FogBugz API key"
                    className="bg-white/[0.04] border-white/[0.08]"
                  />
                  <Button
                    size="sm"
                    onClick={handleStoreFogbugzKey}
                    disabled={!fogbugzKey.trim() || storeFogbugzKey.isPending}
                    className="w-full"
                  >
                    {storeFogbugzKey.isPending ? "Saving..." : "Save API Key"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Azure DevOps — below FogBugz, same pattern (PI-SPEC-SW-1359) */}
        <AdoPatSection />
      </div>

      <CreateApiKeyDialog open={isCreateKeyDialogOpen} onOpenChange={setIsCreateKeyDialogOpen} />

      <div className="text-center text-xs text-muted-foreground tracking-[0.2em] uppercase mt-12 pb-4">
        THE TORO COMPANY ©
      </div>
    </div>
  );
}
